/**
 * Theme controller. The user preference is 'system' | 'light' | 'dark'
 * (device-local); the RESOLVED theme is always stamped on
 * <html data-theme="light|dark"> so tokens.css needs a single selector and
 * there is no unstyled flash — initTheme() runs synchronously during module
 * evaluation, before first render.
 *
 * A `?theme=dark|light` query param overrides for the session — useful for
 * screenshots and demo recordings without touching the stored preference.
 */

export type ThemePref = 'system' | 'light' | 'dark'

const KEY = 'concierge.theme'
const META_COLOURS = { light: '#ffffff', dark: '#0b1220' }

let sessionOverride: 'light' | 'dark' | null = null

export function getThemePref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'light' || v === 'dark' ? v : 'system'
  } catch {
    return 'system'
  }
}

export function setThemePref(pref: ThemePref): void {
  try {
    if (pref === 'system') localStorage.removeItem(KEY)
    else localStorage.setItem(KEY, pref)
  } catch {
    // localStorage unavailable — session-only theming still works
  }
  sessionOverride = null
  applyTheme()
}

function systemDark(): boolean {
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches
}

export function resolvedTheme(): 'light' | 'dark' {
  if (sessionOverride) return sessionOverride
  const pref = getThemePref()
  if (pref === 'system') return systemDark() ? 'dark' : 'light'
  return pref
}

function applyTheme(): void {
  const theme = resolvedTheme()
  document.documentElement.dataset.theme = theme
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', META_COLOURS[theme])
}

export function initTheme(): void {
  try {
    const q = new URLSearchParams(location.search).get('theme')
    if (q === 'dark' || q === 'light') sessionOverride = q
  } catch {
    // no-op
  }
  applyTheme()
  // Follow live OS changes while the preference is 'system'.
  if (typeof matchMedia !== 'undefined') {
    matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (getThemePref() === 'system' && !sessionOverride) applyTheme()
    })
  }
}
